'use client'

import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { getActiveProjectId, setActiveProjectId } from '@/lib/apiFetch'

interface ActiveProjectValue {
  activeProjectId: string | null
  setActiveProject: (projectId: string) => Promise<void>
}

const ActiveProjectContext = createContext<ActiveProjectValue | undefined>(undefined)

export function ActiveProjectProvider({ children }: { children: ReactNode }) {
  const [activeProjectId, setId] = useState<string | null>(null)

  useEffect(() => {
    // Adopt any locally-remembered project, else ask the server for the default.
    const local = getActiveProjectId()
    if (local) {
      setId(local)
      return
    }
    fetch('/api/projects/active', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => {
        if (p?.project_id) {
          setActiveProjectId(p.project_id)
          setId(p.project_id)
        }
      })
      .catch(() => {})
  }, [])

  async function setActiveProject(projectId: string) {
    setActiveProjectId(projectId)
    setId(projectId)
    await fetch(`/api/projects/${projectId}/activate`, {
      method: 'POST',
      credentials: 'same-origin',
    }).catch(() => {})
  }

  return (
    <ActiveProjectContext.Provider value={{ activeProjectId, setActiveProject }}>
      {children}
    </ActiveProjectContext.Provider>
  )
}

export function useActiveProject(): ActiveProjectValue {
  const ctx = useContext(ActiveProjectContext)
  if (ctx === undefined) {
    throw new Error('useActiveProject must be used within an ActiveProjectProvider')
  }
  return ctx
}
